import { useQuery } from '~/shared/hooks/query'
import {
  carePlan,
  CarePlanListResponse,
  carePlanEducationResource,
  CarePlanEducationResourceListResponse,
} from '@fireflyhealth/api'

/**
 * Returns the cache key for carePlan.list queries with a patient filter.
 * Use this for cache invalidation: queryClient.invalidateQueries({ queryKey: listCarePlansQueryKey(patientId) })
 */
export const listCarePlansQueryKey = (patientId: number) =>
  carePlan.listQueryKey({ patient: patientId })

/**
 * Fetches care plans for a patient using the generated API client.
 * Returns snake_case response (CarePlanListResponse) - consumers should access
 * fields like `status_category`, `updated_at`, etc.
 */
export const useCarePlans = (variables: { patientId: number | undefined }) => {
  const { patientId } = variables
  return useQuery(
    patientId ? listCarePlansQueryKey(patientId) : ['listCarePlan', {}],
    async (): Promise<CarePlanListResponse> => {
      if (!patientId) return []
      const response = await carePlan.list({ params: { patient: patientId } })
      return response.data
    },
    { enabled: !!patientId, retryDelay: 500, retry: 3 },
    {
      error: 'Failed to fetch care plans',
    },
  )
}

/**
 * Fetches education resources using the generated API client.
 * Returns snake_case response (CarePlanEducationResourceListResponse).
 */
export const useEducationResources = () => {
  return useQuery(
    carePlanEducationResource.listQueryKey(),
    async (): Promise<CarePlanEducationResourceListResponse> => {
      const response = await carePlanEducationResource.list()
      return response.data
    },
    { retryDelay: 500, retry: 3 },
    {
      error: 'Failed to fetch education resources',
    },
  )
}
